import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { showToast } from 'store/toastSlice';
import { gamesService } from '../api/games';
import { fetchSites } from 'store/sitesSlice';
import { RootState } from 'store';
import SiteSelector from 'components/common/SiteSelector/SiteSelector';
import ProviderSelect from 'components/common/ProviderSelect/ProviderSelect';
import SubProviderSelect from 'components/common/SubProviderSelect/SubProviderSelect';
import styles from './games.module.scss';

const SyncGames: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data: sites, isLoading: sitesLoading } = useAppSelector(
    (state: RootState) => state.sites,
  );

  const [providers, setProviders] = useState<any[]>([]);
  const [loadingProviders, setLoadingProviders] = useState(false);

  const [selectedSite, setSelectedSite] = useState<string | number>('');
  const [selectedProviderId, setSelectedProviderId] = useState<string | number>('');
  const [isSyncing, setIsSyncing] = useState(false);
  const [runId, setRunId] = useState<number | null>(null);
  const [syncStatus, setSyncStatus] = useState<any>(null);

  const [subProviders, setSubProviders] = useState<any[]>([]);
  const [loadingSubProviders, setLoadingSubProviders] = useState(false);
  const [selectedSubProvider, setSelectedSubProvider] = useState<string>('');

  useEffect(() => {
    if (syncStatus) {
      setSyncStatus(null);
      setRunId(null);
    }
  }, [selectedSite, selectedProviderId, selectedSubProvider]);

  useEffect(() => {
    if (!runId) return;

    const interval = setInterval(async () => {
      try {
        const res = await gamesService.getSyncStatus(runId);
        const statusData = res?.data || res;
        setSyncStatus(statusData);

        const stateStr = String(statusData?.status || statusData?.state || '').toLowerCase();
        if (['completed', 'success', 'done', 'failed', 'error'].includes(stateStr)) {
          setIsSyncing(false);
          setRunId(null);
          clearInterval(interval);
        }
      } catch (error) {
        console.error('Failed to get sync status', error);
        setIsSyncing(false);
        setRunId(null);
        clearInterval(interval);
      }
    }, 4000);

    return () => clearInterval(interval);
  }, [runId]);

  useEffect(() => {
    if (!sites || sites.length === 0) {
      dispatch(fetchSites({ page: 1, limit: 100, sortField: 'id', sortValue: 'ASC' }));
    } else if (sites.length > 0 && selectedSite === '') {
      setSelectedSite(sites[0].id);
    }
  }, [sites, selectedSite, dispatch]);

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        setLoadingProviders(true);
        const response = await gamesService.getProviderList();
        if (response?.data) {
          setProviders(response.data);
          if (response.data.length > 0) {
            const defaultProvider =
              response.data.find(
                (p: any) =>
                  p.code?.toLowerCase() === 'softswiss' || p.name?.toLowerCase() === 'softswiss',
              ) || response.data[0];
            setSelectedProviderId(defaultProvider.id);
          }
        }
      } catch (error: any) {
        dispatch(
          showToast({
            text: error.response?.data?.message || 'Failed to fetch providers',
            type: 'error',
          }),
        );
      } finally {
        setLoadingProviders(false);
      }
    };
    fetchProviders();
  }, [dispatch]);

  useEffect(() => {
    if (!selectedProviderId) {
      setSubProviders([]);
      setSelectedSubProvider('');
      return;
    }
    const fetchSubProviders = async () => {
      try {
        setLoadingSubProviders(true);
        const res = await gamesService.getSubProviderList({
          page: 1,
          limit: 100,
          sortField: 'id',
          sortValue: 'DESC',
          providerId: selectedProviderId,
        });
        const list = res?.data?.docs || res?.data?.data || res?.data || [];
        setSubProviders(Array.isArray(list) ? list : []);
        setSelectedSubProvider('');
      } catch (err) {
        console.error('Failed to fetch sub providers', err);
      } finally {
        setLoadingSubProviders(false);
      }
    };
    fetchSubProviders();
  }, [selectedProviderId]);

  const handleSync = async () => {
    if (!selectedSite) {
      dispatch(showToast({ text: 'Please select a site', type: 'error' }));
      return;
    }
    if (!selectedProviderId) {
      dispatch(showToast({ text: 'Please select a provider', type: 'error' }));
      return;
    }

    try {
      setIsSyncing(true);
      const payload: any = {
        siteId: Number(selectedSite),
      };

      const provider = providers.find((p) => p.id === selectedProviderId);
      payload.providerCode = provider?.code || provider?.name || 'softswiss';

      if (selectedSubProvider) payload.providers = [selectedSubProvider];

      const response = await gamesService.syncGames(payload);
      const newRunId = response?.data?.runId;

      if (newRunId) {
        setRunId(newRunId);
        setSyncStatus({ status: 'Starting...' });
        dispatch(showToast({ text: 'Games sync initiated successfully', type: 'success' }));
      } else {
        setIsSyncing(false);
        dispatch(showToast({ text: 'Games sync completed', type: 'success' }));
      }
    } catch (error: any) {
      setIsSyncing(false);
      dispatch(
        showToast({
          text: error.response?.data?.message || 'Failed to sync games',
          type: 'error',
        }),
      );
    }
  };

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Typography variant="h4" className={styles.title}>
          Sync Games
        </Typography>
      </Box>

      <Box
        sx={{
          p: 3,
          backgroundColor: 'var(--bg-panel)',
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          maxWidth: 500,
        }}
      >
        <SiteSelector
          value={selectedSite}
          onChange={(val) => setSelectedSite(val)}
          showAllOption={false}
          minWidth="100%"
        />

        <ProviderSelect
          providers={providers}
          selectedProviderId={selectedProviderId}
          onChange={(val) => setSelectedProviderId(val)}
          disabled={loadingProviders || isSyncing}
        />

        <SubProviderSelect
          subProviders={subProviders}
          selectedValue={selectedSubProvider}
          onChange={(value) => setSelectedSubProvider(value)}
          disabled={loadingSubProviders || isSyncing}
        />

        <Button
          variant="contained"
          onClick={handleSync}
          disabled={isSyncing || sitesLoading || loadingProviders}
          sx={{
            bgcolor: '#2a8039',
            color: '#fff',
            textTransform: 'none',
            '&:hover': {
              bgcolor: '#2a8039',
              opacity: 0.9,
            },
            '&.Mui-disabled': {
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              color: 'rgba(255, 255, 255, 0.3)',
            },
          }}
        >
          {isSyncing ? <CircularProgress size={24} color="inherit" /> : 'Sync Games'}
        </Button>

        {syncStatus && (
          <Box
            sx={{
              mt: 1,
              p: 2,
              bgcolor: 'var(--bg-header)',
              borderRadius: 1,
              border: '1px solid var(--border-color)',
            }}
          >
            {(() => {
              const statusText =
                syncStatus?.status ||
                syncStatus?.state ||
                syncStatus?.message ||
                (typeof syncStatus === 'string' ? syncStatus : JSON.stringify(syncStatus));

              const s = (typeof statusText === 'string' ? statusText : '').toLowerCase();
              let color = 'var(--text-primary)';
              if (s.includes('fail') || s.includes('error')) color = 'error.main';
              else if (s.includes('complete') || s.includes('success')) color = 'success.main';

              return (
                <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                  Status:{' '}
                  <Box
                    component="span"
                    sx={{ color, fontWeight: 600, textTransform: 'capitalize' }}
                  >
                    {statusText}
                  </Box>
                </Typography>
              );
            })()}
            {(syncStatus?.progress !== undefined || syncStatus?.total !== undefined) && (
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mt: 1 }}>
                Progress: {syncStatus?.progress || 0} / {syncStatus?.total || '?'}
              </Typography>
            )}
            {syncStatus?.result && (
              <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mb: 1 }}>
                  Result Summary:
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  {syncStatus.result.synced !== undefined && (
                    <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                      Synced:{' '}
                      <Box component="span" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                        {syncStatus.result.synced}
                      </Box>
                    </Typography>
                  )}
                  {syncStatus.result.created !== undefined && (
                    <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                      Created:{' '}
                      <Box component="span" sx={{ color: 'success.main', fontWeight: 600 }}>
                        {syncStatus.result.created}
                      </Box>
                    </Typography>
                  )}
                  {syncStatus.result.updated !== undefined && (
                    <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                      Updated:{' '}
                      <Box component="span" sx={{ color: 'info.main', fontWeight: 600 }}>
                        {syncStatus.result.updated}
                      </Box>
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SyncGames;
